import { Renderer, Program, Mesh, Color, Triangle, Vec3 } from 'ogl';
import { useEffect, useRef } from 'react';

const vertexShader = `
attribute vec2 uv;
attribute vec2 position;

varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position, 0, 1);
}
`;

const fragmentShader = `
precision highp float;

uniform float uTime;
uniform vec3 uColor;
uniform vec3 uResolution;
uniform vec2 uMouse;
uniform float uAmplitude;
uniform float uSpeed;
uniform float uAlpha;

varying vec2 vUv;

void main() {
  float mr = min(uResolution.x, uResolution.y);
  vec2 uv = (vUv.xy * 2.0 - 1.0) * uResolution.xy / mr;

  uv += (uMouse - vec2(0.5)) * uAmplitude;

  float d = -uTime * 0.5 * uSpeed;
  float a = 0.0;
  for (float i = 0.0; i < 8.0; ++i) {
    a += cos(i - d - a * uv.x);
    d += sin(uv.y * i + a);
  }
  d += uTime * 0.5 * uSpeed;
  vec2 cv = cos(uv * vec2(d, a)) * 0.6 + 0.4;
  vec3 col = vec3(cv.x, cv.y, cos(a + d) * 0.5 + 0.5);
  col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5) * uColor;
  gl_FragColor = vec4(col, uAlpha);
}
`;

/**
 * BG00_mobil: Iridescence（虹色）WebGL背景
 * モバイル時のヒーロー/コンテンツ背景に使用
 */
/** BG00_mobil の共通設定（スピード・透明度など）はここで一元管理 */
const IRIDESCENCE_DEFAULTS = {
  color: [0.9, 1, 1],
  speed: 0.4,
  amplitude: 0.1,
  opacity: 0.5,
  mouseReact: true,
};

export default function Iridescence({
  color = IRIDESCENCE_DEFAULTS.color,
  speed = IRIDESCENCE_DEFAULTS.speed,
  amplitude = IRIDESCENCE_DEFAULTS.amplitude,
  opacity = IRIDESCENCE_DEFAULTS.opacity,
  mouseReact = IRIDESCENCE_DEFAULTS.mouseReact,
  className = '',
  style = {},
  ...rest
}) {
  const ctnDom = useRef(null);
  const mousePos = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    if (!ctnDom.current) return;
    const ctn = ctnDom.current;
    const useAlpha = opacity < 1;
    const renderer = new Renderer({ alpha: useAlpha });
    const gl = renderer.gl;
    if (useAlpha) {
      gl.clearColor(0, 0, 0, 0);
    } else {
      gl.clearColor(1, 1, 1, 1);
    }

    let program;

    function resize() {
      const w = Math.max(1, ctn.offsetWidth || 1);
      const h = Math.max(1, ctn.offsetHeight || 1);
      renderer.setSize(w, h);
      if (program) {
        program.uniforms.uResolution.value.set(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height);
      }
    }
    window.addEventListener('resize', resize);
    const ro = new ResizeObserver(resize);
    ro.observe(ctn);

    const geometry = new Triangle(gl);
    program = new Program(gl, {
      vertex: vertexShader,
      fragment: fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new Color(...color) },
        uResolution: {
          value: new Vec3(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height),
        },
        uMouse: { value: new Float32Array([mousePos.current.x, mousePos.current.y]) },
        uAmplitude: { value: amplitude },
        uSpeed: { value: speed },
        uAlpha: { value: opacity },
      },
    });

    const mesh = new Mesh(gl, { geometry, program });
    let animateId;

    function update(t) {
      animateId = requestAnimationFrame(update);
      program.uniforms.uTime.value = t * 0.001;
      renderer.render({ scene: mesh });
    }

    const canvas = gl.canvas;
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    ctn.appendChild(canvas);

    resize();
    requestAnimationFrame(() => {
      resize();
      animateId = requestAnimationFrame(update);
    });

    function handleMouseMove(e) {
      const rect = ctn.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1.0 - (e.clientY - rect.top) / rect.height;
      mousePos.current = { x, y };
      program.uniforms.uMouse.value[0] = x;
      program.uniforms.uMouse.value[1] = y;
    }
    if (mouseReact) {
      ctn.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (animateId) cancelAnimationFrame(animateId);
      window.removeEventListener('resize', resize);
      ro.disconnect();
      if (mouseReact) {
        ctn.removeEventListener('mousemove', handleMouseMove);
      }
      if (canvas.parentNode === ctn) {
        ctn.removeChild(canvas);
      }
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [color, speed, amplitude, opacity, mouseReact]);

  return (
    <div
      ref={ctnDom}
      className={`w-full h-full ${className}`.trim()}
      style={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        position: 'absolute',
        inset: 0,
        ...style,
      }}
      {...rest}
    />
  );
}
